/**
 * A caixa de diálogo do SM, sobre o `Dialog` do Radix.
 *
 * Papel: reproduz `.modal-overlay`, `.modal`, `.modal-header`, `.close-x`,
 * `.modal-body` e `.modal-footer` (l. 370–394, 401): véu escuro na janela
 * inteira, caixa alinhada ao topo a 40 px, até 560 px de largura, raio de 12 px,
 * cabeçalho e rodapé em `--superficie-2`. O Radix dá o foco preso e o
 * `aria-modal`.
 *
 * Como usar: quem monta a caixa a monta só quando ela está aberta, e a desmonta
 * para fechar — `onFechar` é chamado pelo "✕", e o "Cancelar" do `rodape` é de
 * quem monta. `Esc` e clique no véu **não** fecham, como na origem, que não tem
 * ouvinte de teclado nem de clique no véu: uma caixa que fechasse por eles
 * jogaria fora o que a pessoa digitou num toque acidental.
 *
 * A caixa nasce num portal no fim do `<body>`, fora do `QuadroEmbutido`, e por
 * isso o véu leva a classe do tema: sem ela, a borda, o véu e a sombra sairiam
 * com os valores do `:root`, que são os de Finanças.
 */
import { Dialog as DialogPrimitive } from 'radix-ui';
import { type ReactNode } from 'react';

import { CLASSE_DO_TEMA, useTemaEmbutido } from '@/shared/embutido';
import { cn } from '@/shared/lib/utils';

interface CaixaDeDialogoProps {
  /** O título, que é também o nome acessível da caixa. */
  titulo: string;
  /** Chamado pelo "✕". */
  onFechar: () => void;
  /** Os botões do rodapé, na ordem da origem. */
  rodape: ReactNode;
  /** Os campos. */
  children: ReactNode;
}

/**
 * A caixa de diálogo, aberta enquanto montada.
 *
 * @param titulo - O texto do cabeçalho, em Fraunces 17 px.
 * @param onFechar - Chamado pelo "✕".
 * @param rodape - Os botões, alinhados à direita.
 * @param children - O corpo, numa grade de uma coluna com 12 px entre as linhas.
 * @returns O véu com a caixa.
 */
export function CaixaDeDialogo({ titulo, onFechar, rodape, children }: CaixaDeDialogoProps) {
  const tema = useTemaEmbutido();

  return (
    <DialogPrimitive.Root
      open
      onOpenChange={(aberta) => {
        if (!aberta) onFechar();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            CLASSE_DO_TEMA[tema],
            'fixed inset-0 z-[100] flex items-start justify-center bg-emb-veu px-4 pt-10 pb-4',
          )}
        >
          <DialogPrimitive.Content
            aria-describedby={undefined}
            onEscapeKeyDown={(evento) => evento.preventDefault()}
            onPointerDownOutside={(evento) => evento.preventDefault()}
            onInteractOutside={(evento) => evento.preventDefault()}
            className="max-h-[90vh] w-full max-w-[560px] overflow-y-auto rounded-[12px] bg-superficie shadow-emb-modal outline-none"
          >
            <div className="flex items-center justify-between rounded-t-[12px] border-b border-emb-borda bg-superficie-2 px-5 py-3.5">
              <DialogPrimitive.Title asChild>
                <h3 className="m-0 font-serif text-[17px] font-medium text-foreground">
                  {titulo}
                </h3>
              </DialogPrimitive.Title>
              <button
                type="button"
                aria-label="Fechar"
                onClick={onFechar}
                className="cursor-pointer border-0 bg-transparent [padding:revert] [font-family:revert] text-[18px] text-texto-fraco hover:text-foreground"
              >
                ✕
              </button>
            </div>
            <div className="grid gap-3 px-5 py-[18px]">{children}</div>
            <div className="flex justify-end gap-2 rounded-b-[12px] border-t border-emb-borda bg-superficie-2 px-5 py-3.5">
              {rodape}
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
